import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { DatePicker, Radio } from '@maxtropy/components';
import { RadioChangeEvent } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import styles from './index.module.scss';

const { RangePicker } = DatePicker;

export type RangeValue = [Dayjs | null, Dayjs | null] | null;

// 时间颗粒度
export enum DateType {
  MONTH = 'M',
  YEAR = 'Y',
  CUSTOM = 'C',
}

export interface TimeSelectorRef {
  timeResolution: DateType;
  rangeTime: {
    startTime: number;
    endTime: number;
  };
}

export interface TimeSelectorProps {
  onChange?: (data: TimeSelectorRef) => void;
}

const TimeSelector = forwardRef<TimeSelectorRef, TimeSelectorProps>((props, ref) => {
  const { onChange } = props;

  // 时间颗粒度
  const [timeResolution, setTimeResolution] = useState<DateType>(DateType.MONTH);

  // 月维度 - 默认上月
  const [monthValue, setMonthValue] = useState<Dayjs | null>(dayjs().subtract(1, 'month'));

  // 年维度 - 默认本年
  const [yearValue, setYearValue] = useState<Dayjs | null>(dayjs());

  // 自定义维度 - 默认近31天包含当天
  const [customValue, setCustomValue] = useState<RangeValue>([
    dayjs().subtract(30, 'day').startOf('day'), // 从31天前改为30天前，以便包含当天为第31天
    dayjs().endOf('day'), // 结束日期为今天
  ]);
  const [dates, setDates] = useState<RangeValue>(null);

  // 当前时间范围
  const [rangeTime, setRangeTime] = useState<{
    startTime: number;
    endTime: number;
  }>({
    startTime: 0,
    endTime: 0,
  });

  // 暴露给父组件
  useImperativeHandle(ref, () => ({
    timeResolution,
    rangeTime,
  }));

  // 更新时间范围
  useEffect(() => {
    let newRangeTime = { startTime: 0, endTime: 0 };
    if (timeResolution === DateType.MONTH && monthValue) {
      newRangeTime = {
        startTime: monthValue.startOf('month').valueOf(),
        endTime: monthValue.endOf('month').valueOf(),
      };
    } else if (timeResolution === DateType.YEAR && yearValue) {
      // 对于年份选择，如果选择的是今年，则结束日期为今天23:59:59；如果是往年，则为该年年底
      const selectedYear = yearValue.year();
      const currentYear = dayjs().year();

      newRangeTime = {
        startTime: yearValue.startOf('year').valueOf(),
        endTime:
          selectedYear === currentYear
            ? dayjs().endOf('day').valueOf() // 如果是今年，结束时间为今天23:59:59
            : yearValue.endOf('year').valueOf(), // 如果是往年，结束时间为该年年底23:59:59
      };
    } else if (timeResolution === DateType.CUSTOM && customValue?.[0] && customValue?.[1]) {
      newRangeTime = {
        startTime: customValue[0].startOf('day').valueOf(),
        endTime: customValue[1].endOf('day').valueOf(),
      };
    }

    setRangeTime(newRangeTime);
    onChange?.({ timeResolution, rangeTime: newRangeTime });
  }, [timeResolution, monthValue, yearValue, customValue]);

  // 切换时间颗粒度
  const handleResolutionChange = (e: RadioChangeEvent) => {
    const value = e.target.value as DateType;
    setTimeResolution(value);

    // 切换到月时，设置默认值为上月
    if (value === DateType.MONTH) {
      setMonthValue(dayjs().subtract(1, 'month'));
    }
    // 切换到年时，设置默认值为本年
    else if (value === DateType.YEAR) {
      setYearValue(dayjs());
    }
    // 切换到自定义时，设置默认值为近31天包含当天
    else if (value === DateType.CUSTOM) {
      setCustomValue([dayjs().subtract(30, 'day').startOf('day'), dayjs().endOf('day')]);
    }
  };

  // 月选择器禁用日期 - 不能选择下个月及以后
  const disabledMonthDate = (current: Dayjs) => {
    return current && current >= dayjs().add(1, 'month').startOf('month');
  };

  // 年选择器禁用日期 - 不能选择明年及以后
  const disabledYearDate = (current: Dayjs) => {
    return current && current >= dayjs().add(1, 'year').startOf('year');
  };

  // 自定义日期选择器禁用日期 - 最大范围半年，不能选择明天及以后（即允许选择今天）
  const customDisabledDate = (current: Dayjs) => {
    if (!dates || !current) {
      return current && current > dayjs().startOf('day'); // 只禁止选择明天及以后
    }

    // 如果只选择了开始日期，检查结束日期是否超过半年
    if (dates[0] && !dates[1]) {
      const monthDiff = current.diff(dates[0], 'month', true); // 使用浮点数计算月份差
      return monthDiff > 6 || current > dayjs().startOf('day'); // 只禁止选择明天及以后
    }

    // 如果只选择了结束日期，检查开始日期是否超过半年
    if (!dates[0] && dates[1]) {
      const monthDiff = dates[1].diff(current, 'month', true); // 使用浮点数计算月份差
      return monthDiff > 6 || current > dayjs().startOf('day'); // 只禁止选择明天及以后
    }

    // 在同时有两个日期的情况下，通常不会触发单个日期的禁用
    return current > dayjs().startOf('day'); // 只禁止选择明天及以后
  };

  const onOpenChange = (open: boolean) => {
    if (open) {
      setDates([null, null]);
    } else {
      setDates(null);
    }
  };

  const DateTypeDisplay = {
    [DateType.MONTH]: '月',
    [DateType.YEAR]: '年',
    [DateType.CUSTOM]: '自定义',
  };

  return (
    <div className={styles.timeSelector}>
      <Radio.Group onChange={handleResolutionChange} value={timeResolution} buttonStyle="solid">
        <Radio.Button value={DateType.MONTH}>{DateTypeDisplay[DateType.MONTH]}</Radio.Button>
        <Radio.Button value={DateType.YEAR}>{DateTypeDisplay[DateType.YEAR]}</Radio.Button>
        <Radio.Button value={DateType.CUSTOM}>{DateTypeDisplay[DateType.CUSTOM]}</Radio.Button>
      </Radio.Group>

      {timeResolution === DateType.MONTH && (
        <DatePicker
          picker="month"
          style={{ width: 150 }}
          value={monthValue}
          disabledDate={disabledMonthDate}
          onChange={setMonthValue}
          allowClear={false}
        />
      )}

      {timeResolution === DateType.YEAR && (
        <DatePicker
          picker="year"
          style={{ width: 150 }}
          value={yearValue}
          disabledDate={disabledYearDate}
          onChange={setYearValue}
          allowClear={false}
        />
      )}

      {timeResolution === DateType.CUSTOM && (
        <RangePicker
          style={{ width: 280 }}
          value={dates || customValue}
          disabledDate={customDisabledDate}
          onCalendarChange={val => setDates(val)}
          onChange={val => setCustomValue(val)}
          onOpenChange={onOpenChange}
          allowClear={false}
        />
      )}
    </div>
  );
});

TimeSelector.displayName = 'TimeSelector';

export default TimeSelector;
